import { A11yModule } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  afterEveryRender,
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { fokusVerloren } from '../ki-fokus';
import { KiMarkComponent } from '../ki-mark/ki-mark.component';

/**
 * Rechts angedocktes Panel für KI-Ergebnisse (page-ki.jsx:148-166).
 *
 * <p>Der Aufrufer blendet es per {@code @if} ein und auf {@code geschlossen} wieder aus.
 * Beim Entfernen geht der Fokus an das Element zurück, das ihn beim Öffnen hatte —
 * außer ein anderer Dialog hat ihn inzwischen übernommen. Verschwindet das fokussierte Element im Panel
 * (etwa „Erneut versuchen“), holt das Panel den Fokus zurück; sonst führte Tab in den Hintergrund.
 */
@Component({
  selector: 'app-ki-panel',
  imports: [A11yModule, KiMarkComponent, MatIconModule, TranslatePipe],
  templateUrl: './ki-panel.component.html',
  styleUrl: './ki-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiPanelComponent {
  readonly titel = input.required<string>();
  readonly kontext = input<string>();
  readonly geschlossen = output<void>();

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');

  constructor() {
    const document = inject(DOCUMENT);
    const host: HTMLElement = inject(ElementRef).nativeElement;
    const vorherFokussiert = document.activeElement as HTMLElement | null;

    afterNextRender(() => this.panel().nativeElement.focus());
    afterEveryRender(() => {
      if (fokusVerloren(document)) this.panel().nativeElement.focus();
    });

    inject(DestroyRef).onDestroy(() => {
      const aktiv = document.activeElement;
      const fokusNochBeimPanel = !aktiv || aktiv === document.body || host.contains(aktiv);
      if (fokusNochBeimPanel) vorherFokussiert?.focus();
    });
  }

  protected escape(event: Event): void {
    event.stopPropagation();
    this.geschlossen.emit();
  }
}
